// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Keeps launched Contract Review jobs visible across navigation and reloads.
 *
 * The shared jobs API remains authoritative for lifecycle state; local storage
 * retains only display context that job records lack, then reconnects unfinished
 * entries to polling instead of creating a second execution state machine.
 */
import { useCallback, useEffect, useState } from 'react'

import { AlertCircle, Check, Loader2 } from 'lucide-react'

export interface WorkspaceRunEntry {
  jobId: string
  agentLabel: string
  documentId: string
  filename: string
  version: string
  startedAt: number
  endedAt?: number
  status: string
}

interface UseWorkspaceRunStatusProps {
  orgId: string
  workspaceId: string
  jobStatuses: Record<string, string>
  enqueueJobs: (jobIds: string[]) => void
}

function readEntries(key: string): WorkspaceRunEntry[] {
  try {
    const value = window.localStorage.getItem(key)
    if (!value) return []
    const parsed: unknown = JSON.parse(value)
    if (!Array.isArray(parsed)) throw new Error('Invalid run status')
    return parsed as WorkspaceRunEntry[]
  } catch {
    window.localStorage.removeItem(key)
    return []
  }
}

export function useWorkspaceRunStatus({
  orgId,
  workspaceId,
  jobStatuses,
  enqueueJobs,
}: UseWorkspaceRunStatusProps) {
  const storageKey = `contract-review-run-status:${orgId}:${workspaceId}`
  const [entries, setEntries] = useState<WorkspaceRunEntry[]>(() => readEntries(storageKey))

  useEffect(() => setEntries(readEntries(storageKey)), [storageKey])

  useEffect(() => {
    if (entries.length === 0) window.localStorage.removeItem(storageKey)
    else window.localStorage.setItem(storageKey, JSON.stringify(entries))
  }, [storageKey, entries])

  useEffect(() => {
    const unfinished = entries
      .filter((entry) => entry.status === 'PENDING' || entry.status === 'RUNNING')
      .map((entry) => entry.jobId)
      .filter((jobId) => jobStatuses[jobId] === undefined)
    if (unfinished.length > 0) enqueueJobs(unfinished)
  }, [entries, enqueueJobs, jobStatuses])

  useEffect(() => {
    setEntries((current) => {
      let changed = false
      const next = current.map((entry) => {
        const status = jobStatuses[entry.jobId]
        if (!status || status === entry.status) return entry
        changed = true
        const terminal = status !== 'PENDING' && status !== 'RUNNING'
        return { ...entry, status, endedAt: terminal ? (entry.endedAt ?? Date.now()) : undefined }
      })
      return changed ? next : current
    })
  }, [jobStatuses])

  const addEntries = useCallback((added: WorkspaceRunEntry[]) => {
    setEntries((current) => {
      const known = new Set(current.map((entry) => entry.jobId))
      return [...current, ...added.filter((entry) => !known.has(entry.jobId))]
    })
  }, [])

  return { entries, addEntries, clearEntries: useCallback(() => setEntries([]), []) }
}

function displayStatus(status: string) {
  if (status === 'ERROR') {
    return {
      label: 'Error',
      className: 'border-red-200 bg-red-50 text-red-600',
      Icon: AlertCircle,
    }
  }
  if (status === 'PENDING' || status === 'RUNNING') {
    return {
      label: 'Running',
      className: 'border-slate-200 bg-slate-100 text-slate-500',
      Icon: Loader2,
    }
  }
  return {
    label: 'Done',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-600',
    Icon: Check,
  }
}

function durationLabel(entry: WorkspaceRunEntry) {
  if (!entry.endedAt) return 'In progress'
  const seconds = Math.max(0, Math.round((entry.endedAt - entry.startedAt) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${seconds % 60}s`
}

export function WorkspaceRunStatus({
  entries,
  onClear,
}: {
  entries: WorkspaceRunEntry[]
  onClear: () => void
}) {
  if (entries.length === 0) return null
  const running = entries.filter((entry) => entry.status === 'PENDING' || entry.status === 'RUNNING').length
  const errors = entries.filter((entry) => entry.status === 'ERROR').length
  const description = running > 0
    ? `${running} of ${entries.length} still running…`
    : errors > 0
      ? `${errors} of ${entries.length} ${errors === 1 ? 'job' : 'jobs'} failed.`
      : `All ${entries.length} ${entries.length === 1 ? 'job' : 'jobs'} complete.`

  const summaryClass = running > 0 ? 'text-slate-500' : errors > 0 ? 'text-red-600' : 'text-emerald-600'
  const dotClass = running > 0 ? 'bg-slate-400 animate-pulse' : errors > 0 ? 'bg-red-500' : 'bg-emerald-500'

  return (
    <section
      className="docreview-run-status mt-6 rounded-xl border border-slate-200 bg-white px-6 pb-2.5 pt-[22px] shadow-[0_1px_3px_rgba(0,0,0,.08)]"
      data-testid="cr-run-status"
      aria-labelledby="cr-run-status-title"
    >
      <header className="mb-4 flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <h2 id="cr-run-status-title" className="text-[17px] font-semibold tracking-[-0.01em] text-slate-900">Run status</h2>
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
              {entries.length} {entries.length === 1 ? 'job' : 'jobs'}
            </span>
          </div>
          <div className={`inline-flex items-center gap-2 text-[13px] ${summaryClass}`}>
            <span className={`h-[7px] w-[7px] rounded-full ${dotClass}`} aria-hidden="true" />
            <span>{description}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="h-[34px] shrink-0 rounded-lg border border-slate-200 bg-white px-3.5 text-[13px] font-medium text-slate-700 shadow-[0_1px_2px_rgba(0,0,0,.04)] transition-[background,border-color,transform] duration-150 hover:border-slate-300 hover:bg-slate-50 active:scale-[.97]"
        >
          Clear
        </button>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[64rem] border-collapse text-[13.5px]">
          <thead className="text-left">
            <tr className="border-b border-slate-200">
              <th className="px-3 py-2 text-xs font-semibold text-slate-500">Agent</th>
              <th className="px-3 py-2 text-xs font-semibold text-slate-500">Document</th>
              <th className="whitespace-nowrap px-3 py-2 text-xs font-semibold text-slate-500">Version</th>
              <th className="px-3 py-2 text-xs font-semibold text-slate-500">Job ID</th>
              <th className="whitespace-nowrap px-3 py-2 text-xs font-semibold text-slate-500">Started</th>
              <th className="whitespace-nowrap px-3 py-2 text-xs font-semibold text-slate-500">Ended</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => {
              const status = displayStatus(entry.status)
              const StatusIcon = status.Icon
              const isRunning = status.label === 'Running'
              return (
                <tr
                  key={entry.jobId}
                  className="docreview-run-row border-b border-slate-100 transition-colors duration-150 hover:bg-slate-50/70"
                  style={{ animationDelay: `${Math.min(index, 12) * 32}ms` }}
                >
                  <td className="px-3 py-[13px] align-top font-medium leading-[1.35] text-slate-900">{entry.agentLabel}</td>
                  <td className="min-w-[220px] px-3 py-[13px] align-top">
                    <div className="leading-[1.4] text-slate-900">{entry.filename}</div>
                    <div className="mt-0.5 font-mono text-[11.5px] text-slate-400">{entry.documentId}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-[13px] align-top text-slate-600">{entry.version}</td>
                  <td className="min-w-[170px] max-w-[210px] break-all px-3 py-[13px] align-top font-mono text-xs leading-[1.5] text-slate-500">{entry.jobId}</td>
                  <td className="whitespace-nowrap px-3 py-[13px] align-top text-slate-600">{new Date(entry.startedAt).toLocaleString()}</td>
                  <td className="whitespace-nowrap px-3 py-[13px] align-top">
                    {entry.endedAt ? (
                      <>
                        <div className="text-slate-600">{new Date(entry.endedAt).toLocaleString()}</div>
                        <div className="mt-0.5 text-[11px] text-slate-400">{durationLabel(entry)}</div>
                      </>
                    ) : (
                      <span className="animate-pulse text-[12.5px] text-slate-400">{durationLabel(entry)}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-[13px] text-right align-top">
                    <span className={`docreview-run-badge inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-xs font-semibold ${status.className}`}>
                      <StatusIcon className={`h-3 w-3 ${isRunning ? 'animate-spin' : ''}`} aria-hidden="true" />
                      {status.label}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
